import React, { useState, useEffect } from "react"
import {
  Button,
  HStack,
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverPositioner,
  VStack,
  Text,
  Separator,
} from "@chakra-ui/react"
import { Checkbox } from "@hydrologiq/shared/chakra/Checkbox"
import { FaFilter, FaCheck } from "react-icons/fa6"
import { usageHeading } from "./usageCsv"
import { type RowType } from "./rows"

export interface FieldSelectorProps {
  /** All available field names */
  availableFields: (keyof RowType)[]
  /** Currently selected field names */
  selectedFields: (keyof RowType)[]
  /** Callback when field selection changes */
  onFieldsChange: (fields: (keyof RowType)[]) => void
  /** Default field selection for reset */
  defaultFields: (keyof RowType)[]
}

/**
 * Field selector component for usage reports
 * Allows users to select which fields to include in preview and download
 */
export function FieldSelector({ availableFields, selectedFields, onFieldsChange, defaultFields }: FieldSelectorProps) {
  const [open, setOpen] = useState(false)
  const [tempSelection, setTempSelection] = useState<(keyof RowType)[]>(selectedFields)

  // Sync temp selection with prop changes
  useEffect(() => {
    setTempSelection(selectedFields)
  }, [selectedFields])

  const handleToggleField = (field: keyof RowType) => {
    setTempSelection((prev) => {
      if (prev.includes(field)) {
        return prev.filter((f) => f !== field)
      } else {
        return [...prev, field]
      }
    })
  }

  const handleSelectAll = () => {
    // Select all selectable fields (start and end are always included separately)
    setTempSelection(availableFields)
  }

  const handleDeselectAll = () => {
    // Always keep start and end, clear others
    setTempSelection(["start", "end"])
  }

  const handleReset = () => {
    // Reset to defaults, ensuring start and end are included
    const resetFields = [...defaultFields]
    if (!resetFields.includes("start")) resetFields.unshift("start")
    if (!resetFields.includes("end")) resetFields.splice(1, 0, "end")
    setTempSelection(resetFields)
  }

  const handleApply = () => {
    // Always ensure start and end are included in the final selection
    const finalSelection = [...tempSelection]
    if (!finalSelection.includes("start")) finalSelection.unshift("start")
    if (!finalSelection.includes("end")) {
      const startIndex = finalSelection.indexOf("start")
      finalSelection.splice(startIndex + 1, 0, "end")
    }
    onFieldsChange(finalSelection)
    setOpen(false)
  }

  const handleCancel = () => {
    setTempSelection(selectedFields)
    setOpen(false)
  }

  // Filter out start and end from selectable fields (they're always included)
  const selectableFields = availableFields.filter((field) => field !== "start" && field !== "end")
  const selectableSelectedFields = selectedFields.filter((field) => field !== "start" && field !== "end")
  const tempSelectableFields = tempSelection.filter((field) => field !== "start" && field !== "end")

  const selectedCount = selectableSelectedFields.length
  const totalCount = selectableFields.length

  // Check if at least one selectable field is selected
  const hasSelectableFields = tempSelectableFields.length > 0

  return (
    <PopoverRoot open={open} onOpenChange={(e) => setOpen(e.open)} positioning={{ placement: "bottom-end" }}>
      <PopoverTrigger>
        <Button variant="hydrologiq_secondary" size="sm">
          <HStack gap={2}>
            <FaFilter />
            <Text>
              Fields ({selectedCount}/{totalCount})
            </Text>
          </HStack>
        </Button>
      </PopoverTrigger>

      <PopoverPositioner>
        <PopoverContent width="300px">
          <PopoverBody>
            <VStack align="stretch" gap={3}>
              {/* Header with actions */}
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">Select fields</Text>
                <HStack gap={1}>
                  <Button size="xs" variant="hydrologiq_primary" onClick={handleSelectAll}>
                    All
                  </Button>
                  <Button size="xs" variant="hydrologiq_primary" onClick={handleDeselectAll}>
                    None
                  </Button>
                  <Button size="xs" variant="hydrologiq_primary" onClick={handleReset}>
                    Reset
                  </Button>
                </HStack>
              </HStack>

              <Separator />

              {/* Field list with checkboxes */}
              <VStack align="stretch" gap={2} maxHeight="300px" overflowY="auto">
                {selectableFields.map((field) => {
                  const isSelected = tempSelection.includes(field)
                  return (
                    <Checkbox
                      key={field}
                      checked={isSelected}
                      onCheckedChange={(e) => handleToggleField(field)}
                      alignItems="flex-start"
                    >
                      <Text fontSize="sm">{usageHeading(field)}</Text>
                    </Checkbox>
                  )
                })}
              </VStack>

              <Separator />

              {/* Footer with Apply/Cancel */}
              <HStack justifyContent="flex-end" gap={2}>
                <Button size="sm" variant="hydrologiq_secondary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button size="sm" variant="hydrologiq_primary" onClick={handleApply} disabled={!hasSelectableFields}>
                  <HStack gap={1}>
                    <FaCheck />
                    <Text>Apply</Text>
                  </HStack>
                </Button>
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </PopoverPositioner>
    </PopoverRoot>
  )
}

export default FieldSelector
